import React from 'react';
import PropTypes from 'prop-types';
import {
  Body,
  Box,
  Flex,
  Heading,
  RawMoney,
} from '@edreamsodigeo/prisma-design-system';
import {
  FilledNumberChronology,
  EmptyNumberChronology,
  ChronologyContent,
} from '@/components/Chronology';
import { Trans, useTranslation } from '@edreamsodigeo/frontend-core';
import {
  PriceOfferAlert,
  PriceOfferHeaderSubtitle,
  StepSubtitle,
  StyledPrice,
} from '@/components/PriceOffer/PriceOffer.utils';
import { FreePill } from '@/components/FreePill/FreePill';
import { usePriceFreezeNonPrimeOffer } from '@/providers/PriceFreezeMultiOfferProvider';

const PillStyledPrice = ({ money }) => (
  <RawMoney rounded type="none" size="tiny" currency={money.currency}>
    {money.amount}
  </RawMoney>
);

export const PriceOffer = ({ money = {}, days, noFeeKey }) => {
  return (
    <>
      <PriceOfferDescription />
      <PriceOfferSteps money={money} days={days} noFeeKey={noFeeKey} />
    </>
  );
};

export const PriceOfferNoDescription = ({ money = {}, days, noFeeKey }) => {
  return <PriceOfferSteps money={money} days={days} noFeeKey={noFeeKey} />;
};

const PriceOfferDescription = () => {
  const { t } = useTranslation();

  return (
    <Box mb="base" mt="1x_small">
      <Heading type="3" weight="medium">
        {t('pricefreeze.valueprop.header')}
      </Heading>

      <PriceOfferHeaderSubtitle />
    </Box>
  );
};

const PrimeFirstStep = ({ money = {}, noFeeKey }) => {
  const { t } = useTranslation();
  const nonPrimeOffer = usePriceFreezeNonPrimeOffer();
  const fullDepositPrice = nonPrimeOffer?.offer?.deposit || {};
  return (
    <FilledNumberChronology number={1}>
      <ChronologyContent
        heading={t('pricefreeze.valueprop.step1.heading')}
        title={
          money.amount !== 0 ? (
            <Trans
              i18nKey="pricefreeze.valueprop.step1.title"
              components={{ price: <StyledPrice money={money} /> }}
            />
          ) : (
            t('freeze.flight.price')
          )
        }
        active
        pill={
          money.amount === 0 ? (
            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-end"
            >
              <FreePill animated={false}>
                <Trans
                  i18nKey={'pricefreeze.free.pill.title'}
                  components={{
                    priceKey: <PillStyledPrice money={money} />,
                  }}
                />
              </FreePill>
              <Box ml="2x_small">
                <Body type="xSmall" color="neutral.70">
                  {t('freeze.with.prime')}
                </Body>
              </Box>
            </Flex>
          ) : (
            <></>
          )
        }
        nonPrimePrice={
          money.amount === 0 ? (
            <Body
              inline
              type="small"
              weight="normal"
              color="primary.90"
              sx={{
                textDecoration: 'line-through',
                marginRight: '8px',
              }}
            >
              {fullDepositPrice?.currency ? (
                <RawMoney
                  rounded
                  type="none"
                  size="tiny"
                  strikeThrough="negative"
                  currency={fullDepositPrice.currency}
                >
                  {fullDepositPrice.amount}
                </RawMoney>
              ) : (
                <></>
              )}
            </Body>
          ) : (
            <></>
          )
        }
      >
        {money.amount !== 0 ? (
          <StepSubtitle color="neutral.70">
            {t('pricefreeze.valueprop.step1.total.subtitle')}
          </StepSubtitle>
        ) : null}
        {money.amount !== 0 ? <PriceOfferAlert noFeeKey={noFeeKey} /> : null}
      </ChronologyContent>
    </FilledNumberChronology>
  );
};

const PriceOfferSteps = ({ money = {}, days, noFeeKey }) => {
  const { t } = useTranslation();
  return (
    <Box pl="2x_small">
      <PrimeFirstStep money={money} noFeeKey={noFeeKey} />
      <EmptyNumberChronology number={2} last={true}>
        <ChronologyContent
          heading={t('pricefreeze.valueprop.step2.heading', {
            days: `${days}`,
          })}
          title={
            money.amount !== 0
              ? t('pricefreeze.valueprop.step2.title')
              : t('book.trip')
          }
        >
          {money.amount !== 0 ? (
            <StepSubtitle>
              <Box mb="base">
                {t('pricefreeze.valueprop.step2.total.subtitle')}
              </Box>
            </StepSubtitle>
          ) : null}
        </ChronologyContent>
      </EmptyNumberChronology>
    </Box>
  );
};

PriceOffer.propTypes = {
  money: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  days: PropTypes.number.isRequired,
  noFeeKey: PropTypes.element,
};
